/**
 * DamyxActionExecutor - Sistema de Ejecución de Acciones JSON
 * Permite a DamyxIA ejecutar código, generar previews, diagramas y charts
 * mediante comandos JSON en sus respuestas.
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  Play, Square, AlertCircle, CheckCircle2, Code, 
  RefreshCw, Copy, Check, Terminal, Loader2,
  BarChart3, PieChart, LineChart, GitBranch
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';

// Colores del sistema de diseño
const COLORS = {
  background: '#0F172A',
  panel: '#1E293B',
  border: '#334155',
  textPrimary: '#FFFFFF',
  textSecondary: '#CBD5E1',
  primary: '#3B82F6',
  success: '#22C55E',
  warning: '#EAB308',
  error: '#EF4444',
  info: '#38BDF8',
};

// =====================================================
// EJECUTOR DE CÓDIGO JAVASCRIPT (Sandbox Seguro)
// =====================================================
export const JavaScriptExecutor = ({ code, autoRun = true, onResult }) => {
  const { language } = useLanguage();
  const [output, setOutput] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [hasAutoRun, setHasAutoRun] = useState(false);
  const outputRef = useRef(null);

  // Ejecutar código en sandbox
  const executeCode = async () => {
    setIsRunning(true);
    setOutput([]);
    setError(null);

    try {
      // Crear sandbox con console interceptado
      const logs = [];
      const sandboxConsole = {
        log: (...args) => logs.push({ type: 'log', content: args.map(formatArg).join(' ') }),
        info: (...args) => logs.push({ type: 'info', content: args.map(formatArg).join(' ') }),
        warn: (...args) => logs.push({ type: 'warn', content: args.map(formatArg).join(' ') }),
        error: (...args) => logs.push({ type: 'error', content: args.map(formatArg).join(' ') }),
        table: (data) => logs.push({ type: 'table', content: JSON.stringify(data, null, 2) }),
        clear: () => logs.length = 0,
      };

      // Funciones helper seguras para el sandbox
      const safeFetch = async (url, options) => {
        try {
          const response = await fetch(url, options);
          return response;
        } catch (e) {
          throw new Error(`Fetch error: ${e.message}`);
        }
      };

      const safeSetTimeout = (fn, ms) => {
        if (ms > 5000) ms = 5000; // Límite de 5 segundos
        return setTimeout(fn, ms);
      };

      // Crear función con el código del usuario
      const AsyncFunction = Object.getPrototypeOf(async function(){}).constructor;
      const userFunction = new AsyncFunction(
        'console', 'fetch', 'setTimeout', 'Math', 'Date', 'JSON', 'Array', 'Object', 'String', 'Number', 'Boolean', 'Promise',
        `
        try {
          ${code}
        } catch (e) {
          console.error('Error:', e.message);
          throw e;
        }
        `
      );

      // Ejecutar con timeout
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout: La ejecución excedió 10 segundos')), 10000)
      );

      const executionPromise = userFunction(
        sandboxConsole, 
        safeFetch, 
        safeSetTimeout,
        Math, Date, JSON, Array, Object, String, Number, Boolean, Promise
      );

      const result = await Promise.race([executionPromise, timeoutPromise]);

      // Si hay un resultado explícito, agregarlo
      if (result !== undefined) {
        logs.push({ type: 'result', content: formatArg(result) });
      }

      setOutput(logs);
      if (onResult) onResult({ success: true, logs, result });

    } catch (err) {
      const errorMsg = err.message || 'Error desconocido';
      setError(errorMsg);
      setOutput([{ type: 'error', content: errorMsg }]);
      if (onResult) onResult({ success: false, error: errorMsg });
    } finally {
      setIsRunning(false);
    }
  };

  // Formatear argumentos para mostrar
  const formatArg = (arg) => {
    if (arg === null) return 'null';
    if (arg === undefined) return 'undefined';
    if (typeof arg === 'object') {
      try {
        return JSON.stringify(arg, null, 2);
      } catch {
        return String(arg);
      }
    }
    return String(arg);
  };

  // Auto-ejecutar si está habilitado (solo una vez)
  useEffect(() => {
    if (autoRun && code && !hasAutoRun) {
      setHasAutoRun(true);
      // Pequeño delay para que el usuario vea el código primero
      const timer = setTimeout(() => {
        executeCode();
      }, 500);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRun, code, hasAutoRun]);

  // Scroll al final del output
  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [output]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success(language === 'es' ? 'Código copiado' : 'Code copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(language === 'es' ? 'Error al copiar' : 'Copy failed');
    }
  };

  const getOutputIcon = (type) => {
    switch (type) {
      case 'error': return <AlertCircle className="w-3 h-3 text-red-400" />;
      case 'warn': return <AlertCircle className="w-3 h-3 text-yellow-400" />;
      case 'info': return <AlertCircle className="w-3 h-3 text-blue-400" />;
      case 'result': return <CheckCircle2 className="w-3 h-3 text-emerald-400" />;
      default: return null;
    }
  };

  const getOutputColor = (type) => {
    switch (type) {
      case 'error': return 'text-red-400';
      case 'warn': return 'text-yellow-400';
      case 'info': return 'text-blue-400';
      case 'result': return 'text-emerald-400';
      default: return 'text-zinc-300';
    }
  };

  return (
    <div 
      className="rounded-xl overflow-hidden border"
      style={{ backgroundColor: COLORS.panel, borderColor: COLORS.border }}
      data-testid="js-executor"
    >
      {/* Header */}
      <div 
        className="flex items-center justify-between px-4 py-3 border-b"
        style={{ borderColor: COLORS.border }}
      >
        <div className="flex items-center gap-3">
          <div 
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: '#F59E0B20' }}
          >
            <Terminal className="w-4 h-4 text-amber-400" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-white">
              {language === 'es' ? 'Ejecutor JavaScript' : 'JavaScript Executor'}
            </h3>
            <p className="text-xs text-zinc-500">
              {language === 'es' ? 'Sandbox seguro' : 'Secure sandbox'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleCopy}
            className="p-2 rounded-lg hover:bg-zinc-700 transition-colors"
            title={language === 'es' ? 'Copiar código' : 'Copy code'}
          >
            {copied ? (
              <Check className="w-4 h-4 text-emerald-400" />
            ) : (
              <Copy className="w-4 h-4 text-zinc-400" />
            )}
          </button>
          <button
            onClick={executeCode}
            disabled={isRunning}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              isRunning 
                ? 'bg-zinc-700 text-zinc-400 cursor-not-allowed' 
                : 'bg-emerald-600 hover:bg-emerald-500 text-white'
            }`}
            data-testid="run-code-btn"
          >
            {isRunning ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {language === 'es' ? 'Ejecutando...' : 'Running...'}
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                {language === 'es' ? 'Ejecutar' : 'Run'}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Code Display */}
      <div className="p-4 border-b" style={{ borderColor: COLORS.border }}>
        <pre 
          className="text-sm font-mono text-zinc-300 overflow-x-auto max-h-40 scrollbar-thin"
          style={{ backgroundColor: COLORS.background }}
        >
          <code>{code}</code>
        </pre>
      </div>

      {/* Output */}
      <div 
        ref={outputRef}
        className="p-4 min-h-[80px] max-h-48 overflow-y-auto scrollbar-thin"
        style={{ backgroundColor: COLORS.background }}
      >
        {output.length === 0 && !isRunning && (
          <p className="text-xs text-zinc-500 italic">
            {language === 'es' 
              ? 'Presiona "Ejecutar" para ver el resultado...' 
              : 'Press "Run" to see the output...'}
          </p>
        )}
        
        {isRunning && (
          <div className="flex items-center gap-2 text-zinc-400">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">
              {language === 'es' ? 'Ejecutando código...' : 'Running code...'}
            </span>
          </div>
        )}

        {output.map((log, idx) => (
          <div key={idx} className="flex items-start gap-2 mb-1">
            {getOutputIcon(log.type)}
            <pre className={`text-sm font-mono whitespace-pre-wrap break-all ${getOutputColor(log.type)}`}>
              {log.content}
            </pre>
          </div>
        ))}
      </div>
    </div>
  );
};

// =====================================================
// GENERADOR DE GRÁFICOS DESDE JSON
// =====================================================
export const ChartFromJSON = ({ chartType, data, title, config = {} }) => {
  const { language } = useLanguage();
  
  // Este componente se integra con ChartRenderer existente
  // Solo prepara los datos para el formato esperado
  const chartConfig = useMemo(() => {
    return {
      type: chartType || 'line',
      data: data || [],
      title: title || (language === 'es' ? 'Gráfico Generado' : 'Generated Chart'),
      ...config
    };
  }, [chartType, data, title, config, language]);

  return (
    <div 
      className="rounded-xl overflow-hidden border p-4"
      style={{ backgroundColor: COLORS.panel, borderColor: COLORS.border }}
      data-testid="chart-from-json"
    >
      <div className="flex items-center gap-2 mb-4">
        {chartType === 'bar' && <BarChart3 className="w-5 h-5 text-blue-400" />}
        {chartType === 'pie' && <PieChart className="w-5 h-5 text-purple-400" />}
        {chartType === 'line' && <LineChart className="w-5 h-5 text-emerald-400" />}
        <span className="text-sm font-medium text-white">{chartConfig.title}</span>
      </div>
      
      {/* Placeholder - Se integra con ChartCard existente */}
      <div className="bg-zinc-900 rounded-lg p-4 min-h-[200px] flex items-center justify-center">
        <p className="text-zinc-500 text-sm">
          {language === 'es' 
            ? `Gráfico ${chartType} con ${data?.length || 0} puntos de datos`
            : `${chartType} chart with ${data?.length || 0} data points`}
        </p>
      </div>
    </div>
  );
};

// =====================================================
// PARSER DE ACCIONES JSON - ROBUSTO V2
// =====================================================
export const parseJSONActions = (content) => {
  const actions = {
    execute: [],
    preview: [],
    component: [],
    diagram: [],
    chart: [],
  };

  if (!content) return actions;

  // Función para extraer objetos JSON válidos del texto
  // Maneja correctamente strings con comillas escapadas y objetos anidados
  const extractJSONObjects = (text) => {
    const objects = [];
    let i = 0;
    
    while (i < text.length) {
      if (text[i] === '{') {
        let depth = 0;
        let inString = false;
        let escapeNext = false;
        let start = i;
        
        for (let j = i; j < text.length; j++) {
          const char = text[j];
          
          if (escapeNext) {
            escapeNext = false;
            continue;
          }
          
          if (char === '\\' && inString) {
            escapeNext = true;
            continue;
          }
          
          if (char === '"' && !escapeNext) {
            inString = !inString;
            continue;
          }
          
          if (!inString) {
            if (char === '{') depth++;
            if (char === '}') depth--;
            
            if (depth === 0) {
              objects.push(text.substring(start, j + 1));
              i = j;
              break;
            }
          }
        }
      }
      i++;
    }
    
    return objects;
  };

  // Función para procesar un objeto JSON parseado
  const processAction = (parsed, raw) => {
    if (!parsed || !parsed.type) return;
    
    // Verificar duplicados
    const isDuplicate = (type, check) => {
      return actions[type]?.some(a => check(a));
    };

    switch (parsed.type) {
      case 'execute':
        if (parsed.code && !isDuplicate('execute', a => a.code === parsed.code)) {
          actions.execute.push({
            code: parsed.code,
            language: parsed.language || 'javascript',
            autoRun: parsed.autoRun !== false,
            raw
          });
        }
        break;
        
      case 'component':
        if (parsed.component && !isDuplicate('component', a => a.component === parsed.component)) {
          actions.component.push({
            component: parsed.component,
            props: parsed.props || {},
            raw
          });
        }
        break;
        
      case 'preview':
        if (parsed.code && !isDuplicate('preview', a => a.code === parsed.code)) {
          actions.preview.push({
            code: parsed.code,
            language: parsed.language || 'jsx',
            raw
          });
        }
        break;
        
      case 'diagram':
        if (parsed.content && !isDuplicate('diagram', a => a.content === parsed.content)) {
          actions.diagram.push({
            content: parsed.content,
            diagramType: parsed.diagramType || 'mermaid',
            title: parsed.title || 'Diagram',
            raw
          });
        }
        break;
        
      case 'chart':
        if (parsed.data && !isDuplicate('chart', a => JSON.stringify(a.data) === JSON.stringify(parsed.data))) {
          actions.chart.push({
            data: parsed.data,
            chartType: parsed.chartType || 'line',
            title: parsed.title || 'Chart',
            config: parsed.config || {},
            raw
          });
        }
        break;
    }
  };

  // ESTRATEGIA 1: Extraer y parsear objetos JSON
  const jsonObjects = extractJSONObjects(content);
  
  jsonObjects.forEach(jsonStr => {
    try {
      const parsed = JSON.parse(jsonStr);
      processAction(parsed, jsonStr);
    } catch {
      // Intentar limpiar JSON con comillas simples
      try {
        const cleaned = jsonStr
          .replace(/'/g, '"')
          .replace(/(\w+):/g, '"$1":');
        const parsed = JSON.parse(cleaned);
        processAction(parsed, jsonStr);
      } catch {
        // JSON inválido, ignorar
      }
    }
  });

  // ESTRATEGIA 2: Buscar patrones específicos con regex (fallback)
  // Para casos donde el JSON está malformado pero tiene la estructura correcta
  const typePatterns = [
    { type: 'execute', pattern: /"type"\s*:\s*"execute".*?"code"\s*:\s*"([^"\\]*(?:\\.[^"\\]*)*)"/s },
    { type: 'component', pattern: /"type"\s*:\s*"component".*?"component"\s*:\s*"(\w+)"/s },
  ];

  typePatterns.forEach(({ type, pattern }) => {
    if (actions[type].length === 0) {
      const match = content.match(pattern);
      if (match) {
        if (type === 'execute' && match[1]) {
          actions.execute.push({
            code: match[1].replace(/\\n/g, '\n').replace(/\\"/g, '"'),
            language: 'javascript',
            autoRun: true,
            raw: match[0]
          });
        } else if (type === 'component' && match[1]) {
          actions.component.push({
            component: match[1],
            props: {},
            raw: match[0]
          });
        }
      }
    }
  });

  return actions;
};

// =====================================================
// CONTADOR DE ACCIONES
// =====================================================
export const countActions = (actions) => {
  return (
    actions.execute.length +
    actions.preview.length +
    actions.component.length +
    actions.diagram.length +
    actions.chart.length
  );
};

// =====================================================
// TIPOS DE ACCIONES DISPONIBLES (para documentación)
// =====================================================
export const ACTION_TYPES = {
  execute: {
    name: 'Execute Code',
    nameEs: 'Ejecutar Código',
    description: 'Runs JavaScript code in a secure sandbox',
    descriptionEs: 'Ejecuta código JavaScript en un sandbox seguro',
    icon: Terminal,
    color: 'amber',
  },
  preview: {
    name: 'Preview Component',
    nameEs: 'Vista Previa',
    description: 'Renders React/JSX code in the workspace',
    descriptionEs: 'Renderiza código React/JSX en el workspace',
    icon: Code,
    color: 'emerald',
    example: '{"type": "preview", "code": "const App = () => <div>Hello</div>", "language": "jsx"}'
  },
  component: {
    name: 'Dynamic Component',
    nameEs: 'Componente Dinámico',
    description: 'Renders a registered DamyxIA component',
    descriptionEs: 'Renderiza un componente registrado de DamyxIA',
    icon: Code,
    color: 'purple',
    example: '{"type": "component", "component": "DamyxSunrise", "props": {"mood": "calm"}}'
  },
  diagram: {
    name: 'Generate Diagram',
    nameEs: 'Generar Diagrama',
    description: 'Creates a Mermaid diagram',
    descriptionEs: 'Crea un diagrama Mermaid',
    icon: GitBranch,
    color: 'cyan',
    example: '{"type": "diagram", "content": "graph TD\\nA-->B", "diagramType": "mermaid"}'
  },
  chart: {
    name: 'Generate Chart',
    nameEs: 'Generar Gráfico',
    description: 'Creates a data visualization chart',
    descriptionEs: 'Crea un gráfico de visualización de datos',
    icon: BarChart3,
    color: 'blue',
    example: '{"type": "chart", "data": [{"x": 1, "y": 10}], "chartType": "line"}'
  }
};

export default {
  JavaScriptExecutor,
  ChartFromJSON,
  parseJSONActions,
  countActions,
  ACTION_TYPES
};
